import type { ChronologyResolution } from './chronology.js';
import type { NormalizedChart } from './domain.js';
import { assertNormalizedChartIntegrity } from './chartIntegrity.js';
import { assertCompletePalaceStemTransformations } from './transformationEvidence.js';
import {
  type LocalChartRepository,
  toSavedChartRecord,
  type SavedChartSummary,
} from './localPersistence.js';

export interface SaveProductionNatalChartResult {
  saved: true;
  summary: SavedChartSummary;
  disclosure: string;
}

function assertProductionChronology(resolution: ChronologyResolution | undefined): void {
  if (!resolution) return;
  if (resolution.mode !== 'standard') {
    throw new Error('Production local save accepts Standard Chronology only; Extended research results require a separate research workflow');
  }
  if (resolution.validationStatus !== 'verified_range' || !resolution.inValidatedRange) {
    throw new Error('Production local save requires chronology provenance inside the verified Standard range');
  }
}

function assertSavedAt(savedAt: string): void {
  if (!savedAt.trim()) throw new Error('Production local save requires a non-empty savedAt value');
}

export function saveProductionNatalChart(
  repository: LocalChartRepository,
  chart: NormalizedChart,
  savedAt: string,
  options: Readonly<{ alias?: string; chronology?: ChronologyResolution }> = {},
): SaveProductionNatalChartResult {
  assertSavedAt(savedAt);
  assertNormalizedChartIntegrity(chart);
  assertCompletePalaceStemTransformations(chart);
  assertProductionChronology(options.chronology);

  const record = toSavedChartRecord(chart, savedAt, options.alias, options.chronology);
  repository.save(record);
  const summary = repository.list().find((item) => item.id === record.id);
  if (!summary) throw new Error('Production local save could not verify the persisted chart');

  return Object.freeze({
    saved: true as const,
    summary,
    disclosure: '命盤已明確由使用者操作後保存於此瀏覽器的本機專用 namespace；沒有資料被傳送到網路。',
  });
}
